import { getAccessToken, isAuthenticated, ensureAuth } from '../auth/msal'

export const BASE_URL = (import.meta as any).env?.VITE_API_URL || 'http://localhost:3000'

/**
 * Authenticated fetch — attaches a Bearer token when available and returns
 * the raw Response. Use this when you need response status/body control;
 * prefer the typed `templateApi`/`programApi` helpers otherwise.
 */
export async function authFetch(url: string, options?: RequestInit): Promise<Response> {
  const headers: Record<string, string> = {
    ...options?.headers as Record<string, string>,
  }

  try {
    await ensureAuth()

    if (isAuthenticated()) {
      const token = await getAccessToken()
      if (token) {
        headers['Authorization'] = `Bearer ${token}`
      }
    }
  } catch (e: any) {
    // Auth failed — request will proceed without token
  }

  return fetch(url, { ...options, headers })
}

async function request<T>(path: string, options?: RequestInit): Promise<T> {
  const url = `${BASE_URL}${path}`
  const headers: Record<string, string> = {
    ...options?.headers as Record<string, string>,
  }

  // Only set Content-Type for requests with a body
  if (options?.body) {
    headers['Content-Type'] = 'application/json'
  }

  const response = await authFetch(url, {
    ...options,
    headers,
  })

  if (!response.ok) {
    const body = await response.json().catch(() => ({ error: { message: response.statusText } }))
    throw new Error(body.error?.message || `API Error ${response.status}`)
  }

  if (response.status === 204) return undefined as unknown as T

  const json = await response.json()
  // API wraps responses in { data: ... }
  return (json.data !== undefined ? json.data : json) as T
}

// ── Templates ────────────────────────────────────────────────────────────────

export const templateApi = {
  getCurrent() {
    return request<any>('/v1/templates/current')
  },

  list() {
    return request<any[]>('/v1/templates')
  },
}

// ── Programs ─────────────────────────────────────────────────────────────────

export const programApi = {
  list() {
    return request<any[]>('/v1/programs')
  },

  create(body: { name: string; description?: string; metadata?: Record<string, unknown> }) {
    return request<any>('/v1/programs', {
      method: 'POST',
      body: JSON.stringify(body),
    })
  },

  get(id: string) {
    return request<any>(`/v1/programs/${id}`)
  },

  setFieldValue(programId: string, fieldId: string, body: { value: any; deviationJustification?: string }) {
    return request<any>(`/v1/programs/${programId}/fields/${fieldId}/value`, {
      method: 'PUT',
      body: JSON.stringify(body),
    })
  },

  getDeviations(id: string) {
    return request<any[]>(`/v1/programs/${id}/deviations`)
  },

  delete(id: string) {
    return request<any>(`/v1/programs/${id}`, { method: 'DELETE' })
  },
}
